import { Component, ElementRef, ViewChild } from '@angular/core';
import { Principal } from '../../../../../shared';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatasetService } from '../../../../../shared/dataset/service/dataset.service';
import { StudyEditorContext } from '../../../../study-editor.context';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { AlertService } from '../../../../../shared/alert/alert.service';
import * as XLSX from 'xlsx';
import { HttpErrorResponse } from '@angular/common/http';
import {
    ADD_ENVIRONMENT_DETAILS_VARIABLES_PERMISSIONS,
    ADD_ENVIRONMENTAL_CONDITIONS_VARIABLES_PERMISSIONS,
    ADD_OBSERVATION_SELECTION_VARIABLES_PERMISSIONS,
    ADD_OBSERVATION_TRAIT_VARIABLES_PERMISSIONS,
    ADD_STUDY_SETTINGS_VARIABLES_PERMISSIONS,
    MODIFY_STUDY_SETTINGS_VALUES_PERMISSIONS
} from '../../../../../shared/auth/permissions';
import { ModalConfirmComponent } from '../../../../../shared/modal/modal-confirm.component';
import { ImportStudyMapComponent } from '../import-study-map/import-study-map.component';
import { ImportStudyContext } from '../import-study-context';
import { VariableTypeEnum } from '../../../../../shared/ontology/variable-type.enum';
import { StudyEditorService } from '../../../../study-editor.service';

interface ImportFormat {
    name: string;
    extension: string;
    isVisible: boolean;
}


@Component({
    selector: 'jhi-import-study-modal',
    templateUrl: './import-study-modal.component.html',
    styleUrl: './import-study-modal.component.scss'
})
export class ImportStudyModalComponent {

    @ViewChild('fileUpload') fileUpload!: ElementRef;

    title: string;

    datasetId: number;

    file: File | null = null;
    importedData: any[] | null = null;
    isTransposed = false;

    importFormats: ImportFormat[] = [];
    selectedFormat: ImportFormat | null = null;

    isEnvironmentsImport: boolean = false;
    isStudySettingsImport: boolean = false;

    private destroy$ = new Subject<void>();
    isSaving: boolean;

    constructor(
        public modalService: NgbModal,
        public activeModal: NgbActiveModal,
        private datasetService: DatasetService,
        private principal: Principal,
        private studyEditorContext: StudyEditorContext,
        private alertService: AlertService,
        private router: Router,
        private importStudyContext: ImportStudyContext,
        private translateService: TranslateService,
        private studyEditorService: StudyEditorService
    ) {
    }

    ngOnInit(): void {

        this.isEnvironmentsImport = this.studyEditorContext.getEnvironmentDataset()?.datasetId === this.datasetId;
        this.isStudySettingsImport = this.studyEditorContext.studyDetails.id === this.datasetId;
        this.title = this.isEnvironmentsImport ? this.translateService.instant('study-editor.study-dataset.import-study-modal.title.environment-details') :
            this.isStudySettingsImport ? this.translateService.instant('study-editor.study-dataset.import-study-modal.title.study-settings') :
                this.translateService.instant('study-editor.study-dataset.import-study-modal.title.observations') ;

        this.initImportFormats();
        this.selectedFormat = this.importFormats[1];
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    initImportFormats(): void {
        this.importFormats = [
            { name: 'CSV', extension: '.csv', isVisible: true },
            { name: 'Excel', extension: '.xls,.xlsx', isVisible: true }
        ];
        if (!this.isEnvironmentsImport && !this.isStudySettingsImport) {
            this.importFormats = [...this.importFormats ,
                { name: 'Fieldbook CSV', extension: '.csv', isVisible: true },
                { name: 'Fieldbook Excel', extension: '.xls,.xlsx', isVisible: true  } ];
        }
    }

    backToDatasetOptionModal(): void {
        this.activeModal.dismiss('back');
    }

    clearSelectedFile(): void {
        this.file = null;
        this.importedData = null;
    }

    submitImport(): void {

        this.isSaving = true;

        this.validateNewVariables().then(result => {
            if (result.length > 0) {
                if (!this.isEnvironmentsImport &&
                    (!this.principal.hasAnyAuthorityDirect(ADD_OBSERVATION_TRAIT_VARIABLES_PERMISSIONS) ||
                        !this.principal.hasAnyAuthorityDirect(ADD_OBSERVATION_SELECTION_VARIABLES_PERMISSIONS))) {
                    this.activeModal.close();
                    this.alertService.error('study-editor.study-dataset.import-study-modal.no-permission.observations');
                } else if (this.isEnvironmentsImport &&
                    (!this.principal.hasAnyAuthorityDirect(ADD_ENVIRONMENT_DETAILS_VARIABLES_PERMISSIONS) ||
                        !this.principal.hasAnyAuthorityDirect(ADD_ENVIRONMENTAL_CONDITIONS_VARIABLES_PERMISSIONS))) {
                    this.activeModal.close();
                    this.alertService.error('study-editor.study-dataset.import-study-modal.no-permission.environments');
                } else if (this.isStudySettingsImport &&
                    (!this.principal.hasAnyAuthorityDirect(ADD_STUDY_SETTINGS_VARIABLES_PERMISSIONS) ||
                        !this.principal.hasAnyAuthorityDirect(MODIFY_STUDY_SETTINGS_VALUES_PERMISSIONS))) {
                    this.activeModal.close();
                    this.alertService.error('study-editor.study-dataset.import-study-modal.no-permission.study-settings');
                } else {
                    this.showAddVariableConfirmModal(result, this.datasetId);
                }
            } else {
                if (this.isEnvironmentsImport) {
                    this.importEnvironmentVariableValues();
                } else if (this.isStudySettingsImport) {
                    this.importStudySettingVariableValues();
                } else {
                    this.importObservations(true);
                }
            }
        });
    }

    async validateNewVariables(): Promise<string[]> {
        try {
            const projectProperties = await this.datasetService.getAllProperties(this.studyEditorContext.studyId, this.datasetId).toPromise();
            const importedData = this.importedData![0];
            const newVariables: string[] = [];
            const existingVariableNames: string[] = [];
            const existingVariableAliases: string[] = [];
            const existingNameTypes: string[] = [];

            projectProperties.variables.forEach(e => {
                existingVariableAliases.push(e.alias);
                existingVariableNames.push(e.name);
            });

            projectProperties.nameTypes.forEach(e => {
                existingNameTypes.push(e.code);
            });

            for (let i = 0; i < importedData.length; i++) {
                if (!existingVariableAliases.includes(importedData[i]) &&
                    !existingVariableNames.includes(importedData[i]) &&
                    !existingNameTypes.includes(importedData[i])) {
                    newVariables.push(importedData[i]);
                }
            }

            return newVariables;
        } catch (error) {
            this.isSaving = false;
            return [];
        }
    }

    importObservations(processWarnings: boolean): void {
        this.datasetService.importObservations(this.studyEditorContext.studyId,
            this.datasetId,
            this.importedData,
            processWarnings
        ).toPromise().then(() => {
            this.studyEditorService.navigateToDatasetTab(this.studyEditorContext.studyId, this.datasetId);
            this.alertService.success('study-editor.study-dataset.import-study-modal.import.observation.success');
            this.activeModal.close();
        }, (response: HttpErrorResponse) => {
            if (response.status === 400) {
                this.alertService.error('error.custom', { param: response.error.errors[0].message });
            } else if (response.status === 412) {
                this.showConfirmModal(response.error.errors);
            } else {
                this.alertService.error('error.general');
            }
        }).finally(() => {
            this.isSaving = false;
        });
    }

    importEnvironmentVariableValues(): void {
        this.datasetService.importEnvironmentVariableValues(this.studyEditorContext.studyId,
            this.datasetId,
            this.importedData
        ).toPromise().then(() => {
            this.activeModal.close();
            this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'environments');
            this.alertService.success('study-editor.study-dataset.import-study-modal.import.environment-details.success');
        }).finally(() => {
            this.isSaving = false;
        });
    }

    importStudySettingVariableValues(): void {
        this.datasetService.importStudySettingVariableValues(this.studyEditorContext.studyId,
            this.importedData
        ).toPromise().then(() => {
            this.activeModal.close();
            this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'study-settings');
            this.alertService.success('study-editor.study-dataset.import-study-modal.import.study-settings.success');
        }).finally(() => {
            this.isSaving = false;
        });
    }

    close(): void {
        this.activeModal.close();
    }

    showConfirmModal(warnings: any[]): void {
        this.activeModal.close();
        const warningMessages = warnings.map(warning => warning.message);

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message =
            this.translateService.instant('study-editor.study-dataset.import-study-modal.import.observation.warning', { param: warningMessages });

        confirmModalRef.result.then(() => {
            this.importObservations(false);
        });
    }

    showDesignMapPopup(result: string[], datasetId: number): void {
        this.importStudyContext.datasetId = datasetId;
        this.importStudyContext.isEnvironmentsImportBoolean = this.isEnvironmentsImport;
        this.importStudyContext.isStudySettingsImportBoolean = this.isStudySettingsImport;
        this.importStudyContext.importedData = this.importedData;
        if (this.isStudySettingsImport) {
            this.importStudyContext.variableTypeIds = [VariableTypeEnum.STUDY_DETAIL];
        } else if (this.isEnvironmentsImport) {
            this.importStudyContext.variableTypeIds = [VariableTypeEnum.ENVIRONMENT_DETAIL, VariableTypeEnum.ENVIRONMENT_CONDITION];
        } else {
            this.importStudyContext.variableTypeIds = [VariableTypeEnum.TRAIT, VariableTypeEnum.SELECTION_METHOD];
        }

        // Set the unmapped items to the imported data filtered by the result
        this.importStudyContext.unmappedItems = result.filter(value => value?.trim() !== '')
            .map((value, index) => {
            return {
                id: String(index),
                name: value
            };
        });
        const modalRef = this.modalService.open(ImportStudyMapComponent as Component, { size: 'xl', backdrop: 'static' });
        modalRef.result.then(() => {
            if (this.isEnvironmentsImport) {
                this.importEnvironmentVariableValues();
            } else if (this.isStudySettingsImport) {
                this.importStudySettingVariableValues();
            } else {
                this.importObservations(true);
            }
        });
    }

    showAddVariableConfirmModal(result: string[], datasetId: number): void {
        this.activeModal.close();

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.showButtonIcons = false;
        confirmModalRef.componentInstance.cancelLabel = this.translateService.instant('no');
        confirmModalRef.componentInstance.confirmLabel = this.translateService.instant('yes');
        confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.import-study-modal.import.confirm-mapping');

        confirmModalRef.result.then(() => {
            this.showDesignMapPopup(result, datasetId);
        }, () => {
            if (this.isEnvironmentsImport) {
                this.importEnvironmentVariableValues();
            } else if (this.isStudySettingsImport) {
                this.importStudySettingVariableValues();
            } else {
                this.importObservations(true);
            }
        });
    }

    handleFileChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (!target.files?.length) {
            return;
        }

        const file = target.files[0];
        const reader = new FileReader();

        reader.onload = (e: any) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary', raw: true });

                // grab first sheet or the "Observation" sheet
                let wsname = wb.SheetNames[0];

                if (wb.SheetNames.length > 1) {
                    wsname = 'Observation';

                    if (!wb.Sheets[wsname]) {
                        this.alertService.error('study-editor.study-dataset.import-study-modal.import.wrong-observation-sheet-name');
                        return;
                    }
                }

                // Check if the file is transposed
                const workbook = XLSX.read(bstr, { type: 'binary' });
                let parseData;
                let isTransposed = false;

                if (wsname === 'Observation' && this.isImportedFileTransposed(workbook)) {
                    parseData = this.parseFile(workbook, wsname);
                    isTransposed = true;

                    const result = this.reverseTransposedData(parseData);
                    if (!result.success) {
                        this.alertService.error('error.custom', { param: result.errorMessage || this.translateService.instant('study-editor.study-dataset.import-study-modal.import.error.transpose-file') });
                        return;
                    }
                    parseData = result.data;
                } else {
                    parseData = this.parseFile(wb, wsname);
                }

                // update component state
                this.importedData = parseData;
                this.file = file;
                this.isTransposed = isTransposed;

                // Truncate filename if too long
                const length = 20;
                if (this.file && this.file.name.length > length) {
                    (this.file as any).abbrName = this.file.name.substring(0, length) + '...';
                } else if (this.file) {
                    (this.file as any).abbrName = this.file.name;
                }
            } catch (error) {
                this.alertService.error('study-editor.study-dataset.import-study-modal.import.error.reading-file');
                console.error(error);
            }
        };

        reader.readAsBinaryString(file);
    }

    private parseFile(wb: XLSX.WorkBook, wsname: string): any[] {
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
        return data.map((row: any[]) =>
            row.map((cell: any) => this.stripHtmlTags(cell?.toString() || ''))
        );
    }

    private stripHtmlTags(str: string): string {
        // Remove Tag HTML
        str = str.replace(/<\/?[^>]+(>|$)/g, '');
        // Remove special characters: <, >, ?, % |
        return str.replace(/[<>?%|]/g, '');
    }

    private isImportedFileTransposed(wb: XLSX.WorkBook): boolean {
        const worksheet = wb.Sheets['Observation'];
        return !!worksheet['!merges'];
    }

    private reverseTransposedData(parsedData: any[]): { success: boolean; data?: any[]; errorMessage?: string } {
        if (parsedData.length < 2) {
            return {
                success: false,
                errorMessage: this.translateService.instant('study-editor.study-dataset.import-study-modal.import.error.invalid-transpose-file')
            };
        }

        try {
            const reversedData: any[] = [];
            const observationUnitVarIndex = parsedData[1].findIndex((element: any) =>
                element !== null && element !== '') - 1;

            reversedData.push(this.parseHeaders(parsedData, observationUnitVarIndex));
            this.parseObservations(reversedData, parsedData, observationUnitVarIndex);

            return { success: true, data: reversedData };
        } catch (error) {
            return {
                success: false,
                errorMessage: this.translateService.instant('study-editor.study-dataset.import-study-modal.import.error.transpose-file')
            };
        }
    }

    private parseHeaders(parsedData: any[], observationUnitVarIndex: number): any[] {
        const headers: any[] = [];
        headers.push(...parsedData[0].slice(0, observationUnitVarIndex + 1));
        const nonFactorHeaders = this.getNonFactorHeaders(parsedData);
        if (nonFactorHeaders.length > 0) {
            headers.push(...nonFactorHeaders);
        }
        return headers;
    }

    private parseObservations(reversedData: any[], parsedData: any[], observationUnitVarIndex: number): void {
        const subObservationsPerPlot = this.getSubObservationsPerPlot(parsedData[0]);
        const numberOfSubObsPerPlot = parseInt(subObservationsPerPlot, 10);
        const numberOfObservations = parsedData.length;
        const numberOfNonFactors = this.getNonFactorHeaders(parsedData).length;

        for (let i = 2; i < numberOfObservations; i++) {
            const factorsValues = parsedData[i].slice(0, observationUnitVarIndex);
            for (let observationUnitValue = 1; observationUnitValue <= numberOfSubObsPerPlot; observationUnitValue++) {
                const observationDataRow = [...factorsValues];
                observationDataRow.push(observationUnitValue.toString());

                if (numberOfNonFactors > 0) {
                    const startOfValuesForSubOs = observationUnitVarIndex + 1 + ((observationUnitValue - 1) * numberOfNonFactors);
                    const endOfValuesForSubOs = numberOfNonFactors + startOfValuesForSubOs;
                    const nonFactorValues = parsedData[i].slice(startOfValuesForSubOs, endOfValuesForSubOs);
                    observationDataRow.push(...nonFactorValues);
                }

                reversedData.push(observationDataRow);
            }
        }
    }

    private getNonFactorHeaders(parsedData: any[]): any[] {
        return parsedData[1].filter((element: any, index: number, array: any[]) => {
            return element !== '' && array.indexOf(element) === index;
        });
    }

    private getSubObservationsPerPlot(headers: any[]): string {
        for (let i = headers.length - 1; i >= 0; i--) {
            if (headers[i] !== null && headers[i] !== '') {
                return headers[i];
            }
        }
        return '1'; // Default to 1 if not found
    }

}
